/**
 * What leaves the admin SPA for Sentry: the same rule the edge workers and the dashboard apply.
 *
 * No request headers, cookies or body, no query string or fragment on any URL, no JWT or `Bearer` token in any
 * text, credential-named keys filtered, and only the user id. Pure: returns a copy, never mutates the event.
 */

const FILTERED = '[Filtered]'
const MAX_DEPTH = 10
const MAX_JSON_STRING_LENGTH = 100_000

const SENSITIVE_KEY =
  /authorization|cookie|password|passwd|secret|token|api[-_]?key|apikey|code_verifier|captcha|query_string|stripe-signature|x-supabase-auth/i
/** Span and trace attributes holding a URL's query string or fragment (`http.query`, `url.query`, `http.fragment`). */
const QUERY_OR_FRAGMENT_KEY = /(^|\.)(query|fragment)$/i
/** A bare query string or fragment kept as a value on its own (`?search=...`, `#access_token=...`). */
const BARE_QUERY_OR_FRAGMENT = /^[?#]\S/
const JWT = /\beyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]*/g
const BEARER = /\bBearer\s+[A-Za-z0-9._~+/=-]{8,}/gi
const ABSOLUTE_URL_QUERY = /(https?:\/\/[^\s?#"'<>]+)[?#][^\s"'<>]*/g
const RELATIVE_URL_QUERY = /^(\/[^\s?#]*)[?#]\S*$/

/** The objects currently being walked: meeting one of them again is a cycle. */
type Seen = WeakSet<object>

function scrubString(value: string): string {
  return value
    .replace(JWT, FILTERED)
    .replace(BEARER, `Bearer ${FILTERED}`)
    .replace(ABSOLUTE_URL_QUERY, '$1')
    .replace(RELATIVE_URL_QUERY, '$1')
}

/** A request body arrives as a JSON string: parse it so keys are filtered by name, then write it back. */
function scrubText(value: string, depth: number, seen: Seen): string {
  if (BARE_QUERY_OR_FRAGMENT.test(value)) return FILTERED
  const first = value.trimStart()[0]
  if ((first === '{' || first === '[') && value.length <= MAX_JSON_STRING_LENGTH) {
    try {
      return JSON.stringify(scrubValue(JSON.parse(value), depth + 1, seen))
    } catch {
      // Not JSON after all: scrub it as text.
    }
  }
  return scrubString(value)
}

function scrubValue(value: unknown, depth: number, seen: Seen): unknown {
  if (typeof value === 'string') return scrubText(value, depth, seen)
  if (value === null || typeof value !== 'object') return value
  if (seen.has(value)) return '[Circular]'
  if (depth >= MAX_DEPTH) return '[Truncated]'
  seen.add(value)
  try {
    if (Array.isArray(value)) return value.map((item) => scrubValue(item, depth + 1, seen))
    return Object.fromEntries(
      Object.entries(value).map(([key, child]) => {
        const drop = SENSITIVE_KEY.test(key) || (QUERY_OR_FRAGMENT_KEY.test(key) && child !== undefined && child !== null)
        return [key, drop ? FILTERED : scrubValue(child, depth + 1, seen)]
      }),
    )
  } finally {
    seen.delete(value)
  }
}

/** Method and URL only: headers, cookies, body and query string are not sent. */
function scrubRequest(request: { method?: string; url?: string }): { method?: string; url?: string } {
  return {
    ...(request.method ? { method: request.method } : {}),
    ...(request.url ? { url: scrubString(request.url) } : {}),
  }
}

export function scrubSentryEvent<T extends object>(event: T): T {
  const source = event as Record<string, unknown>
  const out: Record<string, unknown> = scrubValue(
    Object.fromEntries(Object.entries(source).filter(([key]) => key !== 'request' && key !== 'user')),
    0,
    new WeakSet(),
  ) as Record<string, unknown>
  if (source.request && typeof source.request === 'object') {
    out.request = scrubRequest(source.request as { method?: string; url?: string })
  }
  const user = source.user as { id?: string | number } | undefined
  if (user?.id !== undefined) out.user = { id: user.id }
  return out as T
}
